import React from 'react';
import { useGetProduct } from 'hooks/ProductHooks';
import { useGetMenu } from 'hooks/MenuHooks';
import {
  extractApplicationPolicy,
  extractApplicationCommission,
  findGetRootData,
  STORE_URL,
} from 'utils';
import { useDefaultShopDataStack, useUserStack } from 'store';
import { useFileDownload } from 'hooks/FileHooks';
import calculateApplyPolicy from 'utils/productPolicyUtils';

const DEFAULT_GRADE_BENEFIT = {
  discount_rate: 0,
  discount_type: 'R',
  reserve_rate: 0,
  reserve_type: 'R',
};

const useGetPresentData = (idx, type) => {
  const params = {
    join_tables: '["menu_file"]',
  };
  const options = {
    select: (res) => {
      const { status, data, message } = res;

      if (status !== 200) {
        console.warn(message);
        return null;
      }

      return data;
    },
  };

  // NOTE: 썸네일 이미지 다운로드
  const { fileDownload } = useFileDownload();

  const { categories, promotion } = useDefaultShopDataStack();
  const userInfo = useUserStack();

  // NOTE: 상품 데이터 호출
  const { data: Product } = useGetProduct(idx, null, {
    ...options,
    enabled: type === 'product' ? true : false,
  });
  // NOTE: 메뉴 데이터 호출
  const { data: Menu } = useGetMenu(idx, params, {
    ...options,
    enabled: type === 'menu' ? true : false,
  });

  const [base64, setBase64] = React.useState(null);

  /* ===== Function ===== */
  const getBase64Url = React.useCallback(async (key = null) => {
    if (!key || base64) return;

    try {
      const { status, data, message } = await fileDownload({
        file_name: key,
        type: 'url',
      });

      if (status !== 200) {
        console.warn(message);
        return;
      }

      setBase64(data);
    } catch (err) {
      console.error('[getBase64Url] Error!! : ', err);
      return null;
    }
  }, []);

  /**
   * 데이터 JSON 정제
   */
  const RefinedData = React.useMemo(() => {
    if (!Product && !Menu) return null;
    let Data = null;
    // SECTION: 상품 JSON 데이터 정제
    if (type === 'product' && Product) {
      const {
        apply_feedback,
        created_at,
        updated_at,
        delete_reason,
        interruption_reason,
        is_inventory,
        num,
        product_content,
        tag,
        product_detail,
        product_policy,
        grade_benefit_info,
        product_file,
        shop_information,
        product_option,
        forwarding_location,
        ...rest
      } = Product;

      // 썸네일 추출
      const thumbnail = !product_file?.length
        ? null
        : product_file?.find((file) => file.type === 'THUMBNAIL')?.file_url ??
          null;

      if (thumbnail) {
        getBase64Url(thumbnail);
      } else {
        getBase64Url(null);
      }

      // 최상위 카테고리 추출
      const rootCategory = findGetRootData(
        categories,
        rest?.product_category_id
      );

      // 적용된 수수료 추출
      const commissionList = [
        { type: 'BRAND', commission_rate: shop_information?.commission_rate },
        { type: 'CATEGORY', commission_rate: rootCategory?.commission_rate },
      ];
      const applyCommission = extractApplicationCommission(commissionList);

      // 상품 상세와 동일한 기준으로 적용 가능한 상품/등급 정책을 계산합니다.
      const applicationPolicy = product_policy?.length
        ? extractApplicationPolicy(product_policy, rest.price, promotion)
        : null;
      const gradeId = userInfo?.grade_id;
      const gradeBenefit =
        grade_benefit_info && gradeId !== null && gradeId !== undefined
          ? (grade_benefit_info[gradeId] ?? DEFAULT_GRADE_BENEFIT)
          : DEFAULT_GRADE_BENEFIT;
      const applyGradeBenefit = {
        policy_type: 'GRADE',
        ...gradeBenefit,
      };
      const { apply_policy_info, final_discount_price, final_reserve_price } =
        calculateApplyPolicy(
          { price: rest.price },
          applyGradeBenefit,
          applicationPolicy,
        );

      // 필수 옵션만 추출
      const requiredOption = !product_option?.length
        ? []
        : product_option.filter((option) => option.type === 'REQUIRED');

      return {
        ...rest,
        thumbnail: base64,
        origin_thumbnail: thumbnail ? `${STORE_URL}/${thumbnail}` : null,
        shop_information_name: shop_information?.shop_information_name,
        commission: applyCommission,
        apply_grade_benefit: applyGradeBenefit,
        apply_policy: applicationPolicy,
        apply_policy_info,
        final_discount_price,
        final_reserve_price,
        shop_information: shop_information,
        product_option: requiredOption,
        selected_option: !requiredOption.length
          ? null
          : new Array(requiredOption.length).fill(null),
        forwarding_location,
        delivery_price: forwarding_location?.delivery_price ?? 0,
        free_delivery_price: forwarding_location?.free_delivery_price ?? 0,
        // tax_amount: ,
        // tax_free_amount: ,
        // supply_price: ,
      };
    }
    // SECTION: 메뉴 JSON 데이터 정제
    else if (type === 'menu' && Menu) {
      const {
        created_at,
        updated_at,
        allergen_ingredient,
        menu_option,
        exchange_return_policy,
        menu_content,
        nutritional_information,
        order,
        menu_file,
        num,
        ...rest
      } = Menu;

      // 썸네일 추출
      const thumbnail = !menu_file?.length
        ? null
        : menu_file?.find((file) => file.type === 'THUMBNAIL')?.file_url ??
          null;

      if (thumbnail) {
        getBase64Url(thumbnail);
      } else {
        getBase64Url(null);
      }

      return {
        ...rest,
        thumbnail: base64,
        origin_thumbnail: thumbnail ? `${STORE_URL}/${thumbnail}` : null,
        shop_information_name: '제이엠커피그룹',
      };
    }

    return Data;
    // eslint-disable-next-line
  }, [Product, Menu, type, categories, promotion, userInfo?.grade_id, base64]);

  return RefinedData;
};

export default useGetPresentData;

// {
//   "price": 6000,
//   "menu_id": 3,
//   "product_category_id": 1,
//   "tap_category_id": null,
//   "stock_id": null,
//   "menu_name": "아이스 아메리카노",
//   "is_show": "Y",
//   "application_status": "APPROVE",
//   "temperature_option": "ICE",
//   "type": "GENERAL",
//   "sub_menu_name": "Iced Americano",
//   "is_present": "N",
//   "present_information": null,
//   "shop_menu_id": 3,
//   "shop_information_id": 2,
//   "num": 2,
//   "menu_status": "NORMAL",
//   "order": 1,
//   "is_offline": "N",
//   "option_price": 0,
//   "total_price": 6000,
//   "menu_option": [
//     {
//       "option_id": 1,
//       "option_title": "원두",
//       "option_type": "CHOICE",
//       "type": "DEFAULT",
//       "option": [
//         {
//           "option_key": 1,
//           "option_name": "617th에비뉴",
//           "option_type": "CHOICE",
//           "option_price": 0,
//           "count": 0,
//           "default": true
//         }
//       ]
//     },
//     {
//       "option_id": 2,
//       "option_title": "얼음",
//       "option_type": "CHOICE",
//       "type": "DEFAULT",
//       "option": [
//         {
//           "option_key": 2,
//           "option_name": "보통",
//           "option_type": "CHOICE",
//           "option_price": 0,
//           "count": 0,
//           "default": true
//         }
//       ]
//     }
//   ],
//   "thumbnail": "https://s3.ap-northeast-2.amazonaws.com/store.dn/menus/3/아메리카노(ICE).jpg",
//   "is_frequency": "N"
// },
